import { useState } from "react";
import {
  Typography,
  Grid,
  Card,
  Box,
  MenuItem,
  IconButton,
  Tooltip,
  Popover,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { Media } from "types/qbank/media";
import { useMediaModal } from "hooks/mediaModal";
import { useFilter } from "hooks/filter";
import CardImageDetails from "./CardImageDetails";
import CopyLinkButton from "./CopyLinkButton";
import { DeploymentSite } from "types/qbank/deploymentSite";
import { getPublishUrl } from "utilities/mediaUrl";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import Check from "@mui/icons-material/Check";
import { DeployedFile } from "types/qbank/deployedFile";

type IProps = {
  media: Media;
  deploymentSite: DeploymentSite;
};

const MediaCard = ({ media, deploymentSite }: IProps) => {
  const { searchState } = useFilter();
  const { setModal } = useMediaModal();
  const [templateId, setTemplateId] = useState<number>(
    parseInt(searchState.currentCategory.imageTemplateId)
  );
  const publishUrl = getPublishUrl(deploymentSite, media.deployedFiles, templateId);
  const availableTemplates = media.deployedFiles.filter(
    (deployedFile) =>
      deployedFile.deploymentSiteId === deploymentSite.id
  );
  const getDeployedFileName = (deployedFile?: DeployedFile) => {
    try {
      if (!deployedFile)
        return '';

      var deployedFileMetadata = JSON.parse(deployedFile.metadata);
      if (deployedFileMetadata.templateName) {
        return deployedFileMetadata.templateName;
      }
      if (deployedFile.templateName) {
        return deployedFile.templateName;
      }

      if (deployedFileMetadata.width && deployedFileMetadata.height) {
        return deployedFileMetadata.width + " x " + deployedFileMetadata.height;
      }

      return deployedFile.filesize;
    } catch (error: any) {
      console.log(error.message);
      return !deployedFile ? '' : deployedFile.metadata;
    }
  };
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Grid item xs={6} md={4} sx={{
      width: '100%',
      height: '100%'
    }}
    >
      <Card
        elevation={0}
        onDoubleClick={() => setModal({ open: true, media: media })}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative'
        }}>
        <CardImageDetails
            media={media}
            thumbnailSrc={publishUrl || ""}
            publishSrc={publishUrl || ""}
            alt={media.filename}
        />
        <Grid
          container
          item
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          zeroMinWidth
          sx={{
            position: 'absolute',
            bottom: '8px',
            opacity: isHovered ? 1 : 0,
            transition: 'all 0.2s ease-in-out',
            pointerEvents: isHovered ? 'auto' : 'none',
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '2px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: '#023E58',
                color: 'white'
              },
              transition: 'all 0.2s ease-in-out',
              padding: '4px',
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              backgroundColor: 'white',
              marginLeft: '2%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}
          >
            {availableTemplates && availableTemplates.length > 0 && (
              <>
                <Tooltip title="change template">
                  <IconButton
                    aria-label="change template"
                    onClick={handleClick}
                    sx={{
                      color: 'inherit',
                      transition: 'color 0.2s ease-in-out',
                      padding: '2px'
                    }}
                  >
                    <AspectRatioIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  onClick={handleClick}
                  color="inherit"
                  noWrap={true}
                  textOverflow="ellipsis"
                  sx={{
                    transition: 'color 0.2s ease-in-out',
                    fontSize: '0.75rem'
                  }}
                >
                  {
                    getDeployedFileName(availableTemplates.find(
                      (template) => template.imageTemplateId === templateId || (isNaN(templateId) && template.metadata.includes('original'))
                    ))
                  }
                </Typography>
              </>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: '#023E58',
                color: 'white'
              },
              transition: 'all 0.2s ease-in-out',
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              minWidth: 'fit-content',
              backgroundColor: 'white',
              marginRight: '2%'
            }}
          >
            <CopyLinkButton
              media={media}
              disabled={false}
              text={publishUrl || ""}
              imageState={1}
              sx={{ padding: '2px', color: "inherit" }}
            />
          </Box>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            {availableTemplates.map((deployedFile, i) => {
              const active = templateId === deployedFile.imageTemplateId;
              return (
                <MenuItem
                  dense
                  divider
                  key={i}
                  value={deployedFile.imageTemplateId || 1}
                  onClick={() => {
                    setTemplateId(deployedFile.imageTemplateId || NaN);
                    handleClose();
                  }}
                >
                  {active && (
                    <ListItemIcon>
                      <Check />
                    </ListItemIcon>
                  )}
                  <ListItemText inset={!active}>
                    {getDeployedFileName(deployedFile)}
                  </ListItemText>
                </MenuItem>
              );
            })}
          </Popover>
        </Grid>
      </Card>
    </Grid>
  );
};

export default MediaCard;
